/**
 * Notification event types + payload contract for the centralized
 * notification system. The DB stores `event_type` as a free-form string;
 * this enum is the canonical set producers should reference so renderers
 * stay aligned with what's actually written.
 *
 * Payload shapes are declared here too. Renderers are keyed by event type
 * and read fields off `payload` defensively — old DB rows may predate
 * payload-shape changes, so renderers should fall back gracefully on
 * missing fields rather than throw.
 */
export enum NotificationEventType {
  SystemAnnouncement = "system_announcement",
  InvoiceOverdue = "invoice_overdue",
  PaymentReceived = "payment_received",
  DiscountApprovalNeeded = "discount_approval_needed",
  SalesOrderApproved = "sales_order_approved",
  ReportReady = "report_ready",
  WorkflowFailure = "workflow_failure",
  WorkflowRerunLimitExhausted = "workflow_rerun_limit_exhausted",
  WorkflowVersionRolledBack = "workflow_version_rolled_back",
  WorkflowApprovalRequested = "workflow_approval_requested",
  WorkflowNotification = "workflow_notification",
  TaskReassigned = "task_reassigned",
  TaskAgentFollowUp = "task_agent_follow_up",
  DraftSalesOrderCreated = "draft_sales_order_created",
  DraftSalesOrderRevised = "draft_sales_order_revised",
  CollectionThreadAssigned = "collection_thread_assigned",
  CollectionThreadCommented = "collection_thread_commented",
  CollectionThreadStatusChanged = "collection_thread_status_changed",
  CollectionThreadOpened = "collection_thread_opened",
  AgentConversationReplyReceived = "agent_conversation_reply_received",
  AgentConversationNoResponse = "agent_conversation_no_response",
  CommentMention = "comment_mention",
  FeedbackSubmitted = "feedback_submitted",
  FeedbackAssigned = "feedback_assigned",
}

/**
 * Generic action button shape embedded in a notification payload under the
 * reserved `__action_payload__` key. Renderers detect this wrapper and
 * surface buttons inline; clicking dispatches to the named oRPC endpoint
 * with `actionPayload` parsed as JSON.
 */
export interface NotificationActionPayload {
  name: string;
  endpoint: string;
  actionPayload: string;
  buttonType: "primary" | "secondary";
}

export interface NotificationActionPayloadWrapper {
  __action_payload__: NotificationActionPayload[];
}

export const NOTIFICATION_ACTION_PAYLOAD_KEY = "__action_payload__";

/**
 * Why a background agent is pinging a task participant:
 * - `needs_input`: the agent put the task back into `needs_input` and is
 *   waiting on the recipient's answer.
 * - `resolved`: the agent concluded the task; the message is its summary.
 * - `returned_to_agent`: the agent handed the task back to the background
 *   agent that originally escalated it.
 */
export const TASK_AGENT_FOLLOW_UP_KINDS = [
  "needs_input",
  "resolved",
  "returned_to_agent",
] as const;

export type TaskAgentFollowUpKind = (typeof TASK_AGENT_FOLLOW_UP_KINDS)[number];

export interface NotificationPayloadMap {
  [NotificationEventType.SystemAnnouncement]: {
    title: string;
    body: string;
    level?: "info" | "warn" | "critical";
  };
  [NotificationEventType.InvoiceOverdue]: {
    invoiceNumber: string;
    customerName: string;
    amount: number;
    daysOverdue: number;
  };
  [NotificationEventType.PaymentReceived]: {
    paymentNumber: string;
    customerName: string;
    amount: number;
  };
  [NotificationEventType.DiscountApprovalNeeded]: {
    discountId: string;
    customerName: string;
    discountPercent: number;
  };
  [NotificationEventType.SalesOrderApproved]: {
    salesOrderNumber: string;
    customerName: string;
  };
  [NotificationEventType.ReportReady]: {
    reportName: string;
    downloadUrl?: string;
  };
  [NotificationEventType.WorkflowFailure]: {
    workflowId: string;
    workflowName: string;
    runId: string;
    stepId: string | null;
    errorMessage: string;
  } & Partial<NotificationActionPayloadWrapper>;
  /**
   * A workflow stopped rerunning itself because the `snooze_workflow_rerun`
   * node's limit was reached. Terminal for that chain — nothing will retry it
   * automatically, so the owner has to act (hence the rerun action button).
   */
  [NotificationEventType.WorkflowRerunLimitExhausted]: {
    workflowId: string;
    workflowName: string;
    /** The original run the whole rerun chain traces back to. */
    rootRunId: string;
    /** The run whose snooze was refused. */
    lastRunId: string;
    limitReason: "max_reruns" | "expired";
    /** Which attempt was refused (1 = the first rerun). */
    attemptCount: number;
    /** The configured bound: `maxReruns`, or `expiresAfterMinutes`. */
    limit: number;
    /** Why the workflow was snoozing, e.g. "SO not ready". */
    reason: string;
  } & Partial<NotificationActionPayloadWrapper>;
  [NotificationEventType.WorkflowVersionRolledBack]: {
    workflowId: string;
    workflowName: string;
    runId: string;
    brokenVersionId: string;
    brokenVersionNumber: number;
    restoredVersionId: string;
    restoredVersionNumber: number;
    errorMessage: string;
  } & Partial<NotificationActionPayloadWrapper>;
  [NotificationEventType.WorkflowApprovalRequested]: {
    workflowName: string;
    workflowRunId: string;
    stepRunId: string;
    instructions: string;
    /** Gate subject, when the approval has one — lets the inbox deep-link. */
    draftSalesOrderId?: string;
    discountGroupId?: string;
    /** Set (1-based) on periodic reminders for a still-pending gate. */
    reminderAttempt?: number;
  };
  /**
   * Generic informational notification raised by the `notify` workflow
   * step. Title/body are authored in the step config (with `{{var}}`
   * interpolation applied) — no decision or action is attached.
   */
  [NotificationEventType.WorkflowNotification]: {
    title: string;
    body: string;
    /** Scalar items the notify step received (e.g. ERP order numbers). */
    items?: string[];
    /** Customer the subject order belongs to, when resolvable. */
    customerName?: string;
    /** Subject draft sales order, when the step's subjectKind is one. */
    draftSalesOrderId?: string;
    /** Subject discount group (ad-hoc "Notify…" from a group page). */
    discountGroupId?: string;
  };
  [NotificationEventType.TaskReassigned]: {
    taskId: string;
    taskTitle: string;
    /** Reason supplied with the reassignment, if any. */
    reason?: string;
  };
  /**
   * An agent working a task in the background (today: Nami, after a human
   * answered a help request) needs the recipient again, or is reporting how
   * it concluded. Deep-links to the task with the agent chat open so the
   * recipient can pick the conversation up where the agent left it.
   */
  [NotificationEventType.TaskAgentFollowUp]: {
    taskId: string;
    taskTitle: string;
    /** Display name of the agent doing the follow-up (e.g. "Nami"). */
    agentName: string;
    /** The agent's question (`needs_input`) or closing summary (`resolved`). */
    message: string;
    kind: TaskAgentFollowUpKind;
    /** The agent chat thread to resume, when one exists. */
    threadId?: string;
  };
  [NotificationEventType.DraftSalesOrderCreated]: {
    draftSalesOrderId: string;
    customerId: string;
    customerName?: string;
    purchaseOrderNumber?: string;
  };
  /**
   * A customer re-sent a PO with changes on an existing order's email thread:
   * the new draft replaces `previousDraftId` (superseded, or cross-linked when
   * the previous draft was already submitted). Identical re-sends never reach
   * this event — they are discarded at ingestion without a notification.
   */
  [NotificationEventType.DraftSalesOrderRevised]: {
    draftSalesOrderId: string;
    customerId: string;
    customerName?: string;
    purchaseOrderNumber?: string;
    /** Revision number of the new draft (the original is revision 1). */
    revisionNumber: number;
    /** Lines added + removed + changed versus the previous draft. */
    changedLineCount: number;
    /**
     * Customer-visible header fields changed (system-assigned ones such as
     * orderDate excluded). Optional: rows predating this field lack it.
     */
    changedHeaderFieldCount?: number;
    /** The draft this revision replaces (linked as the previous revision). */
    previousDraftId: string;
  };
  [NotificationEventType.CollectionThreadAssigned]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
  };
  [NotificationEventType.CollectionThreadCommented]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Short preview of the comment body. */
    commentPreview?: string;
  };
  [NotificationEventType.CollectionThreadStatusChanged]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** New status after the change ("open" | "resolved"). */
    status: string;
    /** Resolution code when the thread was resolved. */
    resolution?: string;
  };
  [NotificationEventType.CollectionThreadOpened]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** True when opened by automated detection (e.g. short pay). */
    autoDetected?: boolean;
  };
  [NotificationEventType.AgentConversationReplyReceived]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Subject of the customer reply that was filed. */
    replySubject?: string;
    /** The AI-suggested response draft awaiting review, if drafted. */
    draftId?: string | null;
  };
  [NotificationEventType.AgentConversationNoResponse]: {
    collectionThreadId: string;
    referenceCode?: string;
    threadTitle?: string;
    customerName?: string;
    /** Subject of the unanswered outbound email. */
    subject?: string;
    /** Whole days elapsed since the send. */
    daysWaiting: number;
  };
  /**
   * The recipient was @mentioned in a comment ("note") on a record. The
   * event's `notifiableType` / `notifiableId` are the commented record, so the
   * renderer deep-links to it.
   */
  [NotificationEventType.CommentMention]: {
    commentId: string;
    /** Commentable type of the record (e.g. "AP_INVOICE"). */
    commentableType: string;
    commentableId: string;
    /** Short label for the record, e.g. "Invoice INV-1042". */
    entityLabel?: string;
    authorName?: string;
    /** Plain-text preview of the comment, mentions rendered as `@Name`. */
    commentPreview?: string;
  };
  [NotificationEventType.FeedbackSubmitted]: {
    feedbackId: string;
    /** Per-organization ticket number, shown as `FB-<n>`. */
    number?: number;
    /** Display name of the user the report is filed for. */
    reporterName: string;
    /** First ~200 characters of the description. */
    messagePreview: string;
    /** bug | feature_request | question | other */
    kind?: string;
    pageTitle?: string;
    pageUrl?: string;
  };
  /** The recipient was made the owner of a feedback by a triager. */
  [NotificationEventType.FeedbackAssigned]: {
    feedbackId: string;
    /** Per-organization ticket number, shown as `FB-<n>`. */
    number?: number;
    /** Display name of the triager who assigned it. */
    assignedByName: string;
    /** First ~200 characters of the description. */
    messagePreview: string;
    /** bug | feature_request | question | other */
    kind?: string;
  };
}

export type NotificationPayload = NotificationPayloadMap[NotificationEventType];

/**
 * Recipient resolution strategies for NotificationService.create.
 *
 * Producers pass either an explicit user-id list (`recipientUserIds`) or
 * a strategy that the service resolves at send time:
 *   - `all_users`: every active user in the organization.
 *   - `user_groups`: every member of the listed UserGroup IDs.
 *   - `entity_watchers`: every CoreWatch row pointing at the given
 *     `notifiableType` + `notifiableId`, optionally filtered by
 *     `watchRoles`.
 *
 * All strategies resolve at send time, so newly-added users/groups are
 * picked up automatically without re-saving the workflow.
 */
export type NotificationRecipientStrategy =
  "all_users" | "user_groups" | "entity_watchers";
